import { useState, useMemo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { CallApiSignIn, CallApiGetUser } from "../Api/Enforcement";
import {
  CLIENT_AUTH_CHANGED_EVENT,
  OPEN_CLIENT_PROFILE_EVENT,
  handleGetReturnTo,
  handleIsClientAuthenticated,
  handleRemoveReturnTo,
  rememberVerifiedClientUser,
} from "../Share/Func/Storage";
import { verifyClientSession } from "../Share/Func/clientSession";
import { handleClientLogout } from "../Share/Func/clientLogout";
import { useNavigate } from "react-router-dom";
import { ShowCustomErrorModal } from "../Components/Atoms/CustomErrorModal";
import useReCaptcha from "./useReCaptcha";
import { login } from "../Api/SubUrl";

const ERROR_MESSAGES = {
  NETWORK_ERROR: "Thực hiện chưa thành công!\n Liên hệ để được hỗ trợ",
  RECAPTCHA_INVALID: "Đã xảy ra lỗi : \nreCAPTCHA không hợp lệ!",
  GENERIC_ERROR: "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ",
  SESSION_EXPIRED: "Đã hết phiên đăng nhập",
};

export default function useSignInPage() {
  const [showProfile, setShowProfile] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const introduceId = "";

  const {
    version,
    reCaptchaV2,
    isReCaptchaReady,
    isLoadReCaptChaV3,
    recaptchaLoading,
    recaptchaError,
    getReCaptchaToken,
    resetReCaptcha,
    isDefaultV3,
  } = useReCaptcha("login", login);

  const signInSchemaForm = yup.object().shape({
    userName: yup.string().required("Vui lòng nhập tên tài khoản"),
    password: yup.string().required("Vui lòng nhập mật khẩu"),
  });

  const { control, handleSubmit } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: useMemo(
      () => ({
        userName: "",
        password: "",
      }),
      []
    ),
    resolver: yupResolver(signInSchemaForm),
  });

  const showUserProfile = useCallback((profile) => {
    if (!profile?.AccountName) return;
    setUser(profile);
    setShowProfile(true);
  }, []);

  const openProfileFromSession = useCallback(
    (onFail) => {
      CallApiGetUser((res) => {
        if (res?.Status || !res?.AccountName) {
          const isSessionExpired = res?.Status?.status === 401;

          if (isSessionExpired) {
            ShowCustomErrorModal({
              message: `<p>${ERROR_MESSAGES.SESSION_EXPIRED}</p>`,
              title: "Phiên đăng nhập",
            });
          }

          handleClientLogout(() => {
            setUser(null);
            setShowProfile(false);
            if (!isSessionExpired) {
              onFail?.();
            }
          });
          return;
        }

        rememberVerifiedClientUser(res);

        const returnToFromStorage = handleGetReturnTo();
        if (returnToFromStorage && returnToFromStorage !== login) {
          handleRemoveReturnTo();
          navigate(returnToFromStorage, { replace: true });
          return;
        }

        showUserProfile(res);
      });
    },
    [navigate, showUserProfile]
  );

  const handleSignIn = useCallback(
    async (formData) => {
      if (loading) return;

      try {
        const token = await getReCaptchaToken();
        setLoading(true);

        const signInPayload = {
          UserName: formData.userName,
          Password: formData.password,
        };

        CallApiSignIn(token, signInPayload, (res) => {
          setLoading(false);
          resetReCaptcha();

          if (res?.Status) {
            if (res.Status.isNetworkError) {
              ShowCustomErrorModal({
                message: ERROR_MESSAGES.NETWORK_ERROR,
                title: "Đăng nhập thất bại",
              });
              return;
            }

            const status = res.Status.status;
            const message = res.Status.data?.Message;

            if (status === 400) {
              ShowCustomErrorModal({
                message: `<p>${message || "Đăng nhập thất bại"}</p>`,
                title: "Đăng nhập thất bại",
              });
              return;
            }

            if (status === 403) {
              ShowCustomErrorModal({
                message: ERROR_MESSAGES.RECAPTCHA_INVALID,
                title: "Xác thực thất bại",
              });

              return;
            }

            ShowCustomErrorModal({
              message: ERROR_MESSAGES.GENERIC_ERROR,
              title: "Đã xảy ra lỗi",
            });
            return;
          }

          openProfileFromSession(() => {
            ShowCustomErrorModal({
              message: ERROR_MESSAGES.GENERIC_ERROR,
              title: "Đã xảy ra lỗi",
            });
          });
        });
      } catch (error) {
        setLoading(false);
        ShowCustomErrorModal({
          message: error.message || ERROR_MESSAGES.NETWORK_ERROR,
          title: "Đăng nhập thất bại",
        });
      }
    },

    [loading, getReCaptchaToken, resetReCaptcha, openProfileFromSession]
  );

  const handleClose = useCallback(() => {
    setShowProfile(false);
    setUser(null);
  }, []);

  const handleInvalid = useCallback((errors) => {
    const errorMessages = Object.values(errors)
      .map((err) => `<p>${err.message}</p>`)
      .join("");
    ShowCustomErrorModal({
      message: errorMessages,
      title: "Đăng nhập thất bại",
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const openIfLoggedIn = (profile) => {
      if (cancelled || !profile?.AccountName) return;
      showUserProfile(profile);
    };

    verifyClientSession().then(openIfLoggedIn);

    const reopenProfile = () => {
      verifyClientSession({ force: true }).then(openIfLoggedIn);
    };

    window.addEventListener(OPEN_CLIENT_PROFILE_EVENT, reopenProfile);
    return () => {
      cancelled = true;
      window.removeEventListener(OPEN_CLIENT_PROFILE_EVENT, reopenProfile);
    };
  }, [showUserProfile]);

  useEffect(() => {
    const syncFromAuth = () => {
      if (handleIsClientAuthenticated()) return;
      setShowProfile(false);
      setUser(null);
    };

    syncFromAuth();

    window.addEventListener(CLIENT_AUTH_CHANGED_EVENT, syncFromAuth);
    return () =>
      window.removeEventListener(CLIENT_AUTH_CHANGED_EVENT, syncFromAuth);
  }, []);

  return {
    showProfile,
    user,
    control,
    handleSubmit: handleSubmit(handleSignIn, handleInvalid),
    handleClose,
    introduceId,
    version,
    reCaptchaV2,
    isReCaptchaReady,
    isLoadReCaptChaV3,
    recaptchaLoading,
    recaptchaError,
    isDefaultV3,
    loading,
  };
}
